import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../lib/cx';
import { type BottomButton, hasNativeControls, webApp } from '../telegram';

export interface MainButtonState {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

type Kind = 'main' | 'secondary';
type Setter = (kind: Kind, state: MainButtonState | null) => void;

const FallbackContext = createContext<Setter>(() => {});

// Full-screen layers that hide the bottom buttons while they are open (D49).
let hiders = 0;
const hideListeners = new Set<() => void>();
const subscribeHidden = (listener: () => void) => {
  hideListeners.add(listener);
  return () => {
    hideListeners.delete(listener);
  };
};
const buttonsHidden = () => hiders > 0;

/**
 * Hides the screen's bottom buttons while `active`, so a full-screen layer such as the
 * registration scanner shows only its own (D49). They come back as they were once it closes.
 */
export function useHideBottomButtons(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    hiders++;
    for (const listener of hideListeners) listener();
    return () => {
      hiders--;
      for (const listener of hideListeners) listener();
    };
  }, [active]);
}

/**
 * Renders DOM copies of the bottom buttons outside Telegram (plain browser, previews, tests):
 * the secondary one on the left, the main one on the right. Inside Telegram the native
 * MainButton and SecondaryButton are used and this renders nothing.
 */
export function MainButtonHost({ children }: { children: ReactNode }) {
  const [main, setMain] = useState<MainButtonState | null>(null);
  const [secondary, setSecondary] = useState<MainButtonState | null>(null);
  const setter = useRef<Setter>((kind, state) =>
    kind === 'main' ? setMain(state) : setSecondary(state),
  );
  const visible = main || secondary;
  return (
    <FallbackContext.Provider value={setter.current}>
      {children}
      {visible &&
        createPortal(
          <div className="fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-separator/40 bg-bg/95 px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] backdrop-blur">
            {secondary && (
              <button
                type="button"
                data-testid="secondary-button"
                disabled={secondary.disabled || secondary.loading}
                onClick={secondary.onClick}
                className={cx(
                  'min-h-[50px] flex-1 rounded-xl bg-button/12 px-3 text-[17px] font-semibold text-button transition-opacity',
                  (secondary.disabled || secondary.loading) && 'opacity-50',
                )}
              >
                {secondary.text}
              </button>
            )}
            {main && (
              <button
                type="button"
                data-testid="main-button"
                disabled={main.disabled || main.loading}
                onClick={main.onClick}
                className={cx(
                  'min-h-[50px] flex-[2] rounded-xl bg-button px-3 text-[17px] font-semibold leading-tight text-button-text transition-opacity',
                  (main.disabled || main.loading) && 'opacity-50',
                )}
              >
                {main.loading ? '…' : main.text}
              </button>
            )}
          </div>,
          document.body,
        )}
    </FallbackContext.Provider>
  );
}

function useBottomButton(kind: Kind, state: MainButtonState | null): void {
  const setFallback = useContext(FallbackContext);
  const onClickRef = useRef<(() => void) | undefined>(undefined);
  onClickRef.current = state?.onClick;
  const app = webApp();
  const nativeButton: BottomButton | undefined =
    hasNativeControls() && app
      ? kind === 'main'
        ? app.MainButton
        : app.isVersionAtLeast('7.10')
          ? app.SecondaryButton
          : undefined
      : undefined;
  const text = state?.text;
  const disabled = state?.disabled ?? false;
  const loading = state?.loading ?? false;
  const hidden = useSyncExternalStore(subscribeHidden, buttonsHidden, buttonsHidden);
  const visible = state !== null && !hidden;

  useEffect(() => {
    if (nativeButton) {
      if (!visible) {
        nativeButton.hide();
        return;
      }
      const handler = () => {
        if (!disabled && !loading) onClickRef.current?.();
      };
      nativeButton.setParams({
        text: text ?? '',
        is_active: !disabled,
        is_visible: true,
        ...(kind === 'secondary' ? { position: 'left' as const } : {}),
      });
      if (loading) nativeButton.showProgress(false);
      else nativeButton.hideProgress();
      nativeButton.onClick(handler);
      return () => {
        nativeButton.offClick(handler);
        nativeButton.hide();
      };
    }
    if (!visible) {
      setFallback(kind, null);
      return;
    }
    setFallback(kind, {
      text: text ?? '',
      disabled,
      loading,
      onClick: () => onClickRef.current?.(),
    });
    return () => setFallback(kind, null);
  }, [nativeButton, kind, visible, text, disabled, loading, setFallback]);
}

/**
 * The screen's primary action in Telegram's MainButton (docs/UI.md 1.2). Pass null to hide it.
 * Screens never call the SDK directly.
 */
export function useMainButton(state: MainButtonState | null): void {
  useBottomButton('main', state);
}

/** A second bottom action to the left of the main one, e.g. "Назад" in the wizard. */
export function useSecondaryButton(state: MainButtonState | null): void {
  useBottomButton('secondary', state);
}

/**
 * The same as `useMainButton`, as an element: render it conditionally when a parent and a child
 * screen take turns owning the button (a parent's `null` would otherwise hide the child's).
 */
export function MainButton(state: MainButtonState) {
  useBottomButton('main', state);
  return null;
}

/** True when the fallback buttons are drawn in the page and need bottom padding. */
export function usesFallbackMainButton(): boolean {
  return !hasNativeControls();
}
